import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import useCarTowns from "@/hooks/useCarTowns";
import { SegmentedControl, SegmentedControlItem } from "@/components/ds/SegmentedControl";
import { MagnifyingGlass, MapPin, Plus, X, ArrowsDownUp, CaretDown } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useSettings } from "@/context/SettingsContext";
import { DateBox, DateRangeBox } from "@/components/search/DateFields";
import { GuestsRow } from "@/components/search/GuestsRow";
import { readFlightSearch, flightSearchError } from "@/lib/flightSearchContract";
import { pick } from "@/lib/languages";
import { searchFormL } from "@/i18n/search";
import { appShellL } from "@/i18n/appShell";
import { isNativeApp } from "@/lib/nativeApp";

// Quarter hours, the way a rental desk asks when the keys change hands.
const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${["00", "15", "30", "45"][i % 4]}`);

// The words of a menu's chosen option; the first option's when the value is
// one the list does not have (the native list then shows the first too).
const wordFor = (options, value) => (options.find(([key]) => key === value) || options[0])[1];

export default function SearchWidget({ defaultTab = "hotels", compact = false }) {
  const nav = useNavigate();
  const tab = defaultTab;
  const [sp] = useSearchParams();
  const { t, lang } = useSettings();
  const S = pick(searchFormL, lang);
  // The apps draw the form as the approved design's card: the place field
  // under a magnifying glass, icons in the regular weight, the dates in a sheet.
  const native = isNativeApp();
  const sheets = pick(appShellL, lang).sheets;
  const PlaceIcon = native ? MagnifyingGlass : MapPin;
  const iconWeight = native ? "regular" : "bold";
  // The car pick-up offers the towns Toukky has cars in before any address.
  // Read when the field is first used, not on every page that shows the form.
  const [pickupUsed, setPickupUsed] = useState(false);
  const carTowns = useCarTowns({ enabled: tab === "cars" && pickupUsed });

  const today = new Date();
  const t3 = new Date(); t3.setDate(t3.getDate() + 3);
  const t7 = new Date(); t7.setDate(t7.getDate() + 7);
  const t1 = new Date(); t1.setDate(t1.getDate() + 1);

  const parseDate = (s, fallback) => {
    if (!s) return fallback;
    const d = new Date(`${s}T00:00:00`);
    return isNaN(d.getTime()) ? fallback : d;
  };
  const urlPax = parseInt(sp.get("passengers") || sp.get("guests") || sp.get("adults") || "", 10);

  // shared (hydrated from URL so re-searching keeps the current query)
  const [destination, setDestination] = useState(sp.get("city") || "");
  // Cars: a one-way rental returns the car elsewhere. Only cars whose owner
  // allows it (a company's option) are listed then; a private owner's car
  // always comes back where it was collected.
  const [dropoff, setDropoff] = useState(sp.get("dropoff") || "");
  const [oneWay, setOneWay] = useState(Boolean(sp.get("dropoff")));
  const [pickupTime, setPickupTime] = useState(TIMES.includes(sp.get("pickup_time")) ? sp.get("pickup_time") : "10:00");
  const [dropoffTime, setDropoffTime] = useState(TIMES.includes(sp.get("dropoff_time")) ? sp.get("dropoff_time") : "10:00");
  // checkIn/checkOut: the spelling of older shared links.
  // A car is collected at the pick-up time (10:00 unless the link says
  // otherwise); once that time has passed today, the search starts tomorrow,
  // the return still three days after.
  const carStartsTomorrow = () => {
    if (tab !== "cars") return false;
    const [h, m] = (TIMES.includes(sp.get("pickup_time")) ? sp.get("pickup_time") : "10:00").split(":").map(Number);
    return today.getHours() * 60 + today.getMinutes() > h * 60 + m;
  };
  const [checkIn, setCheckIn] = useState(() => parseDate(sp.get("check_in") || sp.get("checkIn"), carStartsTomorrow() ? t1 : today));
  const [checkOut, setCheckOut] = useState(() => {
    const fallback = carStartsTomorrow() ? new Date(t1.getFullYear(), t1.getMonth(), t1.getDate() + 3) : t3;
    return parseDate(sp.get("check_out") || sp.get("checkOut"), fallback);
  });
  // One traveller, not two. Two is a hotel-industry default that arrived here
  // by imitation: it fits a couple booking a room and nobody else, and a person
  // searching for themselves had to notice the mistake and correct it before
  // every price on the page stopped being double what they would pay. Adding a
  // second traveller is one tap; discovering you were quoted for two is not.
  const [adults, setAdults] = useState(Number.isFinite(urlPax) && urlPax > 0 ? urlPax : 1);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(() => { const r = parseInt(sp.get("rooms") || "", 10); return Number.isFinite(r) && r > 0 ? r : 1; });

  // flights (hydrated from URL)
  const [origin, setOrigin] = useState(sp.get("origin_city") || "");
  const [destCity, setDestCity] = useState(sp.get("destination_city") || "");
  const [departDate, setDepartDate] = useState(() => parseDate(sp.get("date"), t1));
  const [returnDate, setReturnDate] = useState(() => {
    const dep = parseDate(sp.get("date"), t1);
    const ret = parseDate(sp.get("return_date"), null);
    if (ret && ret >= dep) return ret;
    const d = new Date(dep); d.setDate(d.getDate() + 7);
    return d;
  });
  const [tripType, setTripType] = useState(["round", "oneway", "multi"].includes(sp.get("trip")) ? sp.get("trip") : "round");
  const [cabin, setCabin] = useState(sp.get("cabin_class") || "economy");
  const [directOnly, setDirectOnly] = useState(sp.get("direct") === "1");
  const [childAges, setChildAges] = useState([]);
  // The customer's word that a child under 12 flies with an adult (no adult in the search).
  const [accompanied, setAccompanied] = useState(sp.get("accompanied") === "1");
  // Controlled only so a search refused for the confirmation can open it where it is.
  const [guestsOpen, setGuestsOpen] = useState(false);
  const companionErrorId = "flight-companion-error";
  const [contractError, setContractError] = useState(null);
  const [urlContractError, setUrlContractError] = useState(null);

  // multi-city: array of legs. Minimum 2, maximum 5. Booking.com pattern.
  const [legs, setLegs] = useState(() => [
    { from: "", to: "", date: t1 },
    { from: "", to: "", date: t7 },
  ]);
  const updateLeg = (i, patch) => { setUrlContractError(null); setLegs((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l))); };
  const addLeg = () => {
    setUrlContractError(null);
    setLegs((ls) => {
      if (ls.length >= 5) return ls;
      const date = ls[ls.length - 1].date ? new Date(ls[ls.length - 1].date) : null;
      // Calendar days, not 72 elapsed hours: preserve the date across DST.
      if (date) date.setDate(date.getDate() + 3);
      return [...ls, { from: "", to: "", date }];
    });
  };
  const removeLeg = (i) => { setUrlContractError(null); setLegs((ls) => (ls.length <= 2 ? ls : ls.filter((_, idx) => idx !== i))); };

  // URL changes can reuse this mounted widget (history and results re-search).
  useEffect(() => {
    if (tab !== "flights") return;
    const search = readFlightSearch(sp);
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const week = new Date(); week.setDate(week.getDate() + 7);
    const dateFromUrl = (value, fallback) => {
      if (!value) return fallback;
      const date = new Date(`${value}T00:00:00`);
      return Number.isNaN(date.getTime()) || format(date, "yyyy-MM-dd") !== value ? null : date;
    };
    const dep = dateFromUrl(sp.get("date"), tomorrow);
    const ret = dateFromUrl(sp.get("return_date"), null);
    const defaultReturn = new Date(dep || tomorrow); defaultReturn.setDate(defaultReturn.getDate() + 7);
    setOrigin(sp.get("origin_city") || "");
    setDestCity(sp.get("destination_city") || "");
    setDepartDate(dep);
    setReturnDate(ret && ret >= dep ? ret : defaultReturn);
    setTripType(["round", "oneway", "multi"].includes(sp.get("trip")) ? sp.get("trip") : "round");
    setCabin(sp.get("cabin_class") || "economy");
    setDirectOnly(sp.get("direct") === "1");
    setAdults(Number.isFinite(search.adults) ? search.adults : 0);
    setChildAges(search.childAges);
    setChildren(search.childAges.length);
    setAccompanied(sp.get("accompanied") === "1");
    setLegs(search.legs.length ? search.legs.map(leg => ({ from: leg.from, to: leg.to, date: dateFromUrl(leg.date, null) })) : [
      { from: "", to: "", date: tomorrow }, { from: "", to: "", date: week },
    ]);
    setUrlContractError(search.error);
    setContractError(null);
    setDateError(false);
  }, [sp, tab]);

  const trips = [["round", t("search.roundTrip")], ["oneway", t("search.oneWay")], ["multi", t("search.multiCity")]];
  const cabins = [["economy", t("cabin.economy")], ["premium", t("cabin.premium")], ["business", t("cabin.business")], ["first", t("cabin.first")]];
  const chooseTrip = (k) => { setTripType(k); setUrlContractError(null); };

  const flightGuests = {
    // The apps keep the travellers as a full row of the card.
    phoneMenu: !native,
    childAges,
    accompanied,
    open: guestsOpen,
    onOpenChange: setGuestsOpen,
    companionErrorId: contractError === "companion" ? companionErrorId : undefined,
    setAccompanied: value => { setUrlContractError(null); setContractError(null); setAccompanied(value); },
    setAdults: value => { setUrlContractError(null); setAdults(value); },
    setChildren: count => {
      setUrlContractError(null);
      setChildren(count);
      setChildAges(ages => Array.from({ length: count }, (_, i) => ages[i] ?? ""));
    },
    setChildAges: ages => { setUrlContractError(null); setChildAges(ages); },
  };

  // One panel lets the traveller stop after the first date. Searching then would
  // send an empty check_out or return_date, so the incomplete range is caught
  // here rather than handed to the API.
  const [dateError, setDateError] = useState(false);
  const needsRange = tab === "flights" ? tripType === "round" : true;
  const rangeIncomplete = tab === "flights"
    ? tripType === "round" && !(departDate && returnDate)
    : !(checkIn && checkOut);

  const submit = () => {
    const fmt = (d) => d ? format(d, "yyyy-MM-dd") : "";
    if (needsRange && rangeIncomplete) { setDateError(true); return; }
    setDateError(false);
    if (tab === "hotels" || tab === "flightHotel") {
      const qs = new URLSearchParams({ city: destination, check_in: fmt(checkIn), check_out: fmt(checkOut), guests: String(adults + children), rooms: String(rooms) }).toString();
      nav(`/hotels?${qs}`);
    } else if (tab === "flights") {
      const party = new URLSearchParams({ adults: String(adults), passengers: String(adults + children) });
      const first = tripType === "multi" ? legs[0] : { from: origin, to: destCity, date: departDate };
      party.set("origin_city", first?.from || "");
      party.set("destination_city", first?.to || "");
      party.set("date", fmt(first?.date));
      if (tripType === "round") party.set("return_date", fmt(returnDate));
      if (children) party.set("child_ages", childAges.join(","));
      const alone = adults === 0 && accompanied;
      if (alone) party.set("accompanied", "1");
      if (tripType === "multi") {
        party.set("trip", "multi");
        party.set("legs", legs.map(leg => `${leg.from}|${leg.to}|${fmt(leg.date)}`).join(","));
      }
      const error = urlContractError || readFlightSearch(party).error;
      if (error) {
        setContractError(error);
        // The confirmation lives in the travellers picker: open it on the checkbox.
        if (error === "companion") {
          setGuestsOpen(true);
          setTimeout(() => document.querySelector('[data-testid="flight-accompanied"]')?.focus(), 0);
        }
        return;
      }
      setContractError(null);
      // Preserve legacy adults-only URLs; explicit composition travels separately
      // whenever children or an explicit adults field are present.
      const compositionParams = children || sp.has("adults") ? {
        adults: String(adults), ...(children ? { child_ages: childAges.join(",") } : {}),
        ...(alone ? { accompanied: "1" } : {}),
      } : {};
      if (tripType === "multi") {
        // Primary fields remain for legacy summaries; legs carries the itinerary.
        const firstLeg = legs[0] || {};
        const legsParam = legs.map((l) => `${l.from}|${l.to}|${fmt(l.date)}`).join(",");
        const qs = new URLSearchParams({
          origin_city: firstLeg.from || "",
          destination_city: firstLeg.to || "",
          date: fmt(firstLeg.date),
          passengers: String(adults + children),
          cabin_class: cabin,
          legs: legsParam,
          trip: "multi",
          direct: directOnly ? "1" : "",
          ...compositionParams,
        }).toString();
        nav(`/flights?${qs}`);
      } else {
        const qs = new URLSearchParams({ origin_city: origin, destination_city: destCity, date: fmt(departDate), passengers: String(adults + children), cabin_class: cabin, direct: directOnly ? "1" : "", trip: tripType, ...compositionParams });
        if (tripType === "round") qs.set("return_date", fmt(returnDate));
        nav(`/flights?${qs.toString()}`);
      }
    } else {
      const qs = new URLSearchParams({ city: destination, check_in: fmt(checkIn), check_out: fmt(checkOut), pickup_time: pickupTime, dropoff_time: dropoffTime });
      if (oneWay && dropoff.trim()) qs.set("dropoff", dropoff.trim());
      nav(`/cars?${qs.toString()}`);
    }
  };

  return (
    <form onSubmit={(event) => { event.preventDefault(); submit(); }} data-testid="search-widget" className={`tk-search bg-white rounded-xl border border-touki ${compact ? "" : "shadow-tk-card"}`}>
      <div className={`tk-search-fields ${tab === "flights" ? `tk-flight-search ${tripType === "multi" ? "tk-search-multi" : "tk-search-flights"}` : tab === "cars" ? "tk-search-stays tk-search-cars" : "tk-search-stays"}`}>
        {tab === "flights" && (
          <div className="tk-search-options flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* From a tablet up, one segmented control: the three trips on one row. */}
            <SegmentedControl aria-label={S.tripType} value={tripType} onValueChange={chooseTrip}
              fullWidth className="tk-search-trip-tabs sm:w-auto" data-testid="trip-type">
              {trips.map(([k, l]) => (
                <SegmentedControlItem key={k} value={k} data-testid={`trip-${k}`} className="px-2.5 text-label-md sm:flex-none sm:px-4 sm:text-label-lg">{l}</SegmentedControlItem>
              ))}
            </SegmentedControl>
            {/* On a phone the trip, the travellers and the cabin are three menus
                on one line above the fields, as on the big flight sites (owner,
                2026-10-04), so the cover photograph shows round the search. A
                menu is its words and a caret over the native list, which opens
                the phone's own picker (index.css). */}
            <span className="tk-search-menu tk-search-trip">
              <select aria-label={S.tripType} value={tripType} onChange={(e) => chooseTrip(e.target.value)} data-testid="trip-select">
                {trips.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <span className="tk-search-menu-label" aria-hidden="true">{wordFor(trips, tripType)}</span>
              <CaretDown size={14} weight="bold" className="tk-search-menu-caret" aria-hidden="true" />
            </span>
            <span className="tk-search-menu tk-search-cabin">
              <select aria-label={S.cabin} value={cabin} onChange={(e) => setCabin(e.target.value)} data-testid="cabin-select" className="min-h-11 rounded-lg border border-touki bg-white px-3 py-2 text-label-lg font-semibold text-touki-black">
                {cabins.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <span className="tk-search-menu-label" aria-hidden="true">{wordFor(cabins, cabin)}</span>
              <CaretDown size={14} weight="bold" className="tk-search-menu-caret" aria-hidden="true" />
            </span>
          </div>
        )}

        {/* HOTELS / FLIGHT+HOTEL / CARS — vertical stack */}
        {(tab === "hotels" || tab === "flightHotel" || tab === "cars") && (
          <>
            {/* Destination full-width */}
            <div className="tk-search-destination min-w-0">
              {tab === "cars" ? (
                <div className={`${native ? "tk-search-place " : ""}flex items-center gap-2 px-3 min-h-[56px] rounded-lg bg-white border border-touki focus-within:border-touki-black`}>
                  <PlaceIcon size={18} weight={iconWeight} className="text-touki-muted shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0" onPointerDown={() => setPickupUsed(true)} onInput={() => setPickupUsed(true)}>
                    <span className="block text-label-sm font-medium text-touki-muted" aria-hidden="true">{S.pickupLabel}</span>
                    <AddressAutocomplete value={destination} onChange={setDestination} exact towns={carTowns} lang={lang} placeholder={S.pickupHint} ariaLabel={`${S.pickupLabel} (${S.pickupHint})`} testId="hotel-destination-input" naked />
                  </div>
                </div>
              ) : (
                <LocationAutocomplete
                  value={destination}
                  onChange={setDestination}
                  icon={native ? "search" : "pin"}
                  label={S.destination}
                  placeholder={S.whereTo}
                  testId="hotel-destination-input"
                  stays
                  openOnFocus={!compact}
                />
              )}
              {tab === "cars" && (
                <div className="tk-search-oneway mt-2">
                  <label className="inline-flex min-h-11 items-center gap-2 text-label-lg font-semibold text-touki-black cursor-pointer">
                    <input type="checkbox" data-testid="cars-oneway" checked={oneWay} onChange={(e) => { setOneWay(e.target.checked); if (!e.target.checked) setDropoff(""); }} className="h-4 w-4" />
                    {S.oneWay}
                  </label>
                  {oneWay && (
                    <div className={`${native ? "tk-search-place " : ""}mt-1 flex items-center gap-2 px-3 min-h-[56px] rounded-lg bg-white border border-touki focus-within:border-touki-black`}>
                      <PlaceIcon size={18} weight={iconWeight} className="text-touki-muted shrink-0" aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <span className="block text-label-sm font-medium text-touki-muted" aria-hidden="true">{S.dropoffLabel}</span>
                        <AddressAutocomplete value={dropoff} onChange={setDropoff} exact lang={lang} placeholder={S.pickupHint} ariaLabel={`${S.dropoffLabel} (${S.pickupHint})`} testId="cars-dropoff-input" naked />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dates row */}
            <div className="tk-search-dates min-w-0">
              <DateRangeBox
                labelFrom={tab === "cars" ? S.carFrom : t("search.checkin")}
                labelTo={tab === "cars" ? S.carTo : t("search.checkout")}
                from={checkIn}
                to={checkOut}
                onChange={({ from, to }) => { setCheckIn(from); setCheckOut(to); if (to) setDateError(false); }}
                testIdFrom="hotel-checkin"
                testIdTo="hotel-checkout"
                sheetTitle={tab === "cars" ? sheets.datesRental : tab === "flightHotel" ? sheets.datesTrip : sheets.dates}
                unit={tab === "cars" ? "days" : "nights"}
              />
              {dateError && <p role="alert" className="mt-1 text-label-md text-touki-red" data-testid="date-error">{S.pickBothDates}</p>}
              {tab === "cars" && (
                <div className="tk-search-times mt-2 grid grid-cols-2 gap-2">
                  {[["cars-pickup-time", S.pickupTime, pickupTime, setPickupTime], ["cars-dropoff-time", S.dropoffTime, dropoffTime, setDropoffTime]].map(([id, label, value, set]) => (
                    <label key={id} className="block text-label-sm font-medium text-touki-muted">
                      {label}
                      <select data-testid={id} value={value} onChange={(e) => set(e.target.value)} className="mt-1 block h-11 w-full rounded-lg border border-touki bg-white px-3 text-body-md font-semibold text-touki-black tabular-nums">
                        {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Guest counts apply to accommodation searches, not car rentals. */}
            {tab !== "cars" && <GuestsRow adults={adults} children={children} rooms={rooms} setAdults={setAdults} setChildren={setChildren} setRooms={setRooms} testId="hotel-guests" />}
          </>
        )}

        {/* FLIGHTS — vertical stack */}
        {tab === "flights" && tripType !== "multi" && (
          <>
            {/* The departure, the destination and the dates: on a phone one card,
                the swap button on its first seam; from a tablet up the wrapper
                steps aside (display: contents) and they are the grid's own cells. */}
            <div className="tk-search-route">
              {/* The two fields share a relative box so the swap button can sit on
                  the seam between them. It only exchanges the two existing state
                  values — no new search behaviour. */}
              <div className="tk-search-airports relative min-w-0">
                <div>
                  <LocationAutocomplete
                    value={origin}
                    onChange={setOrigin}
                    icon="takeoff"
                    label={t("search.from")}
                    placeholder={S.cityOrAirport}
                    testId="flight-origin-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { const from = origin; setOrigin(destCity); setDestCity(from); }}
                  data-testid="flight-swap"
                  aria-label={S.swap}
                  title={S.swap}
                  className="tk-search-swap absolute end-2 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full border border-touki-field bg-white text-touki-black inline-flex items-center justify-center hover:border-touki-black transition-colors duration-fast"
                >
                  <ArrowsDownUp size={15} weight="bold" aria-hidden="true" />
                </button>
                <div>
                  <LocationAutocomplete
                    value={destCity}
                    onChange={setDestCity}
                    icon="landing"
                    label={t("search.to")}
                    placeholder={S.cityOrAirport}
                    testId="flight-destination-input"
                  />
                </div>
              </div>
              <div className="tk-search-dates min-w-0">
                {tripType === "round" ? (
                  <DateRangeBox
                    labelFrom={t("search.departure")}
                    labelTo={t("search.return")}
                    from={departDate}
                    to={returnDate}
                    onChange={({ from, to }) => { setDepartDate(from); setReturnDate(to); if (to) setDateError(false); }}
                    testIdFrom="flight-depart"
                    testIdTo="flight-return"
                    sheetTitle={sheets.datesTrip}
                  />
                ) : (
                  // One way has one date, so one calendar: a range panel would
                  // offer an end the search will never send.
                  <DateBox label={t("search.departure")} value={departDate} onChange={setDepartDate} testId="flight-depart" />
                )}
                {dateError && <p role="alert" className="mt-1 text-label-md text-touki-red" data-testid="date-error">{S.pickBothDates}</p>}
              </div>
            </div>
            <GuestsRow adults={adults} children={children} rooms={rooms} setRooms={setRooms} showRooms={false} testId="flight-guests" {...flightGuests} />
            <label className="tk-search-direct inline-flex min-h-11 items-center gap-2 text-label-lg text-touki-black cursor-pointer" data-testid="direct-only-label">
              <Checkbox checked={directOnly} onCheckedChange={(v) => setDirectOnly(!!v)} data-testid="direct-only-checkbox" />
              {t("search.directOnly")}
            </label>
          </>
        )}

        {/* FLIGHTS — multi-city (Booking-style dynamic legs) */}
        {tab === "flights" && tripType === "multi" && (
          <>
            {legs.map((leg, i) => (
              <div key={i} className="rounded-lg border border-touki p-3 space-y-2" data-testid={`leg-${i}`}>
                <div className="flex min-h-11 items-center justify-between">
                  <div className="text-eyebrow font-bold text-touki-muted uppercase">{S.flightN} {i + 1}</div>
                  {legs.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeLeg(i)}
                      data-testid={`leg-remove-${i}`}
                      className="-me-2 inline-flex min-h-11 items-center gap-1 px-2 text-label-md font-semibold text-touki-black underline decoration-touki-field underline-offset-4 hover:decoration-touki-black"
                    >
                      <X size={12} weight="bold" aria-hidden="true" /> {S.remove}
                    </button>
                  )}
                </div>
                <LocationAutocomplete
                  value={leg.from}
                  onChange={(v) => updateLeg(i, { from: v })}
                  icon="takeoff"
                  label={t("search.from")}
                  placeholder={S.cityOrAirport}
                  testId={`leg-from-${i}`}
                />
                <LocationAutocomplete
                  value={leg.to}
                  onChange={(v) => updateLeg(i, { to: v })}
                  icon="landing"
                  label={t("search.to")}
                  placeholder={S.cityOrAirport}
                  testId={`leg-to-${i}`}
                />
                <DateBox
                  label={S.departureDate}
                  value={leg.date}
                  onChange={(d) => updateLeg(i, { date: d })}
                  testId={`leg-date-${i}`}
                  minDate={i > 0 ? legs[i - 1].date : new Date()}
                />
              </div>
            ))}
            {legs.length < 5 && (
              <button
                type="button"
                onClick={addLeg}
                data-testid="leg-add"
                className="w-full min-h-11 rounded-lg border border-dashed border-touki-field text-touki-black hover:border-touki-black font-semibold text-label-lg py-3 transition-colors duration-fast inline-flex items-center justify-center gap-1.5"
              >
                <Plus size={16} weight="bold" aria-hidden="true" /> {S.addFlight}
              </button>
            )}
            <GuestsRow adults={adults} children={children} rooms={rooms} setRooms={setRooms} showRooms={false} testId="flight-guests" {...flightGuests} />
          </>
        )}

        {/* Search button — full width at bottom */}
        {tab === "flights" && contractError && <p role="alert" id={contractError === "companion" ? companionErrorId : undefined} className="text-body-sm text-touki-red">{flightSearchError(contractError, lang)}</p>}
        {/* Red: every search button is red (owner, 2026-10-02). */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          data-testid="search-submit-btn"
          className="tk-search-submit w-full h-auto min-h-14 px-5 text-button-lg font-bold"
        >
          {/* The apps' button is its word alone (the approved design). */}
          {native ? null : <MagnifyingGlass size={20} weight="bold" aria-hidden="true" />} {tab === "flights" ? t("search.searchFlights") : t("search.search")}
        </Button>
      </div>
    </form>
  );
}
